<template>
  <small-modal class="expanded-modal" @close="closeModal">
    <div class="main-wrapper">
      <h1 class="title">Device breakdown</h1>
      <p class="subtitle">
        To adjust the period, exit this modal and pick dates from the top-left
        calendar.
      </p>
      <div class="expanded-pie-container">
        <div class="pie-buttons">
          <button
            v-for="(data, key) in provisionalPieData"
            :key="key"
            :class="{ active: currentPieKey === key }"
            @click="changePieData(key)"
          >
            <span
              class="color-indicator"
              :style="{
                backgroundColor: currentPieKey === key ? data.color : 'grey',
              }"
            ></span>
            {{ getButtonLabel(key) }}
          </button>
        </div>
        <div class="pie-chart-canvas-wrapper">
          <canvas id="expandedDevicePieChart"></canvas>
          <div
            v-if="currentPieData.desktopPercentage > 0 && !isPieChartEmpty"
            class="pie-center-item"
            :style="pieLabelPositions.desktop"
          >
            <DesktopIcon class="pie-icon desktop-icon" />
            {{ currentPieData.desktopPercentage }}%
          </div>
          <div
            v-if="currentPieData.mobilePercentage > 0 && !isPieChartEmpty"
            class="pie-center-item"
            :style="pieLabelPositions.mobile"
          >
            <MobileIcon class="pie-icon" />
            {{ currentPieData.mobilePercentage }}%
          </div>
          <div v-if="isPieChartEmpty" class="no-data-pie">
            No data available
          </div>
        </div>
      </div>
    </div>
  </small-modal>
</template>

<script>
import Chart from "chart.js";
import SmallModal from "@/components/shared/SmallModal.vue";
import DesktopIcon from "@/assets/svg/small-desktop-icon.svg?inline";
import MobileIcon from "@/assets/svg/small-mobile-icon.svg?inline";

export default {
  name: "ExpandedPieChart",
  components: {
    SmallModal,
    DesktopIcon,
    MobileIcon,
  },
  props: {
    provisionalPieData: {
      type: Object,
      required: true,
    },
    currentPieKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      devicePieChart: null,
      pieLabelPositions: {
        desktop: { top: "50%", left: "50%" },
        mobile: { top: "50%", left: "50%" },
      },
    };
  },
  computed: {
    currentPieData() {
      return this.provisionalPieData[this.currentPieKey];
    },
    isPieChartEmpty() {
      return (
        this.currentPieData.desktopData === 0 &&
        this.currentPieData.mobileData === 0
      );
    },
  },
  watch: {
    currentPieKey() {
      this.updateDevicePieChart();
    },
  },
  mounted() {
    this.initExpandedDevicePieChart();
  },
  beforeDestroy() {
    if (this.devicePieChart) {
      this.devicePieChart.destroy();
    }
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    changePieData(key) {
      this.$emit("change-pie-data", key);
    },
    getButtonLabel(key) {
      const labels = {
        videosPlayed: "Videos played",
        widgetDisplays: "Visitors exposed to Vidjet",
        salesGenerated: "Sales Generated",
      };
      return labels[key];
    },
    hexToRgba(hex, alpha) {
      let r = 0,
        g = 0,
        b = 0;
      if (hex.length === 4) {
        r = "0x" + hex[1] + hex[1];
        g = "0x" + hex[2] + hex[2];
        b = "0x" + hex[3] + hex[3];
      } else if (hex.length === 7) {
        r = "0x" + hex[1] + hex[2];
        g = "0x" + hex[3] + hex[4];
        b = "0x" + hex[5] + hex[6];
      }
      return `rgba(${+r},${+g},${+b},${alpha})`;
    },
    updatePieLabelPositions() {
      const chart = this.devicePieChart;
      if (!chart) return;
      const meta = chart.getDatasetMeta(0);
      if (!meta.data || meta.data.length < 2) return;

      if (this.currentPieData.desktopPercentage === 100) {
        this.pieLabelPositions = {
          desktop: { top: "50%", left: "50%" },
          mobile: { display: "none" },
        };
        return;
      }

      if (this.currentPieData.mobilePercentage === 100) {
        this.pieLabelPositions = {
          desktop: { display: "none" },
          mobile: { top: "50%", left: "50%" },
        };
        return;
      }

      const calculatePosition = (slice) => {
        if (!slice || !slice._model) return { top: "50%", left: "50%" };
        const model = slice._model;
        const angle =
          model.startAngle + (model.endAngle - model.startAngle) / 2;
        const radius =
          model.innerRadius + (model.outerRadius - model.innerRadius) / 2;
        const x = model.x + Math.cos(angle) * radius;
        const y = model.y + Math.sin(angle) * radius;
        return { top: `${y}px`, left: `${x}px` };
      };

      this.pieLabelPositions = {
        desktop: calculatePosition(meta.data[0]),
        mobile: calculatePosition(meta.data[1]),
      };
    },
    initExpandedDevicePieChart() {
      const ctx = document
        .getElementById("expandedDevicePieChart")
        .getContext("2d");
      const pieDataConfig = this.getPieChartDataConfig();

      this.devicePieChart = new Chart(ctx, {
        type: "pie",
        data: {
          labels: ["Desktop", "Mobile"],
          datasets: [
            {
              ...pieDataConfig,
              borderColor: "white",
              hoverBorderColor: "white",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          tooltips: { enabled: false },
          cutout: "60%",
          animation: {
            onComplete: () => this.updatePieLabelPositions(),
          },
          onResize: () => this.updatePieLabelPositions(),
        },
      });
    },
    getPieChartDataConfig() {
      if (this.isPieChartEmpty) {
        return {
          data: [100],
          backgroundColor: ["#C4C4C4"],
          borderWidth: 0,
        };
      } else if (this.currentPieData.desktopPercentage === 100) {
        return {
          data: [100],
          backgroundColor: [this.currentPieData.color],
          borderWidth: 0,
        };
      } else if (this.currentPieData.mobilePercentage === 100) {
        return {
          data: [100],
          backgroundColor: [this.currentPieData.color],
          borderWidth: 0,
        };
      } else {
        return {
          data: [
            this.currentPieData.desktopData,
            this.currentPieData.mobileData,
          ],
          backgroundColor: [
            this.hexToRgba(this.currentPieData.color, 0.3),
            this.currentPieData.color,
          ],
          borderWidth: 4,
        };
      }
    },
    updateDevicePieChart() {
      if (this.devicePieChart) {
        const pieDataConfig = this.getPieChartDataConfig();

        this.devicePieChart.data.datasets[0].data = pieDataConfig.data;
        this.devicePieChart.data.datasets[0].backgroundColor =
          pieDataConfig.backgroundColor;
        this.devicePieChart.data.datasets[0].borderWidth =
          pieDataConfig.borderWidth;

        this.devicePieChart.update();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.main-wrapper {
  padding: 32px;
}

.title {
  @include base-font;
  @include font-big;
  font-weight: 600;
  margin-bottom: 12px;
}
.subtitle {
  @include base-font;
  @include font-normal;
  margin-bottom: 24px;
}

.expanded-pie-container {
  display: flex;
  width: 100%;
  height: 70vh;
}

.pie-buttons {
  width: 30%;
  display: flex;
  flex-direction: column;
  padding-right: 20px;
  justify-content: center;

  button {
    @include base-font;
    @include font-smaller;
    font-weight: 600;
    display: flex;
    align-items: center;
    padding: 10px 15px;
    margin-bottom: 10px;
    text-align: left;
    background: white;
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.3s;
    color: $medium-grey;

    &.active {
      color: $dark-grey;
    }
  }
}

.color-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
}

.pie-chart-canvas-wrapper {
  position: relative;
  width: 70%;
  height: 100%;
}

.pie-center-item {
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #333;
  pointer-events: none;
  transition: top 0.5s ease, left 0.5s ease;
}

.pie-icon {
  margin-right: 5px;
}

.desktop-icon {
  margin-top: 5px;
}

.no-data-pie {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-weight: bold;
  color: #333;
  pointer-events: none;
}
</style>

<style lang="scss">
.expanded-modal .small-modal.bigger {
  width: 90vw;
  max-width: 90vw;
  height: 90vh;
  max-height: 90vh;
  overflow: hidden;
}
</style>
